import { Archive, ArchiveRestore, CircleAlert, CircleCheck, KeyRound, Pencil, Plus, RefreshCw, Save, UserRound, X } from 'lucide-react'

import { QueryState } from '@krp/sale-ui'
import { Button, InfoTip, TextField } from '@krp/ui-kit'

import { CommandError, Problem } from '../../nomenclature/ui/parts'
import '../../nomenclature/ui/own-nomenclature.css'
import type { OwnCashiersActions, OwnCashiersVm } from '../model/types'
import { ICON_SIZE, ICON_SIZE_SMALL } from '../../../ownIcons'

/**
 * Окно «Кассиры» собственной кассы: состав кассиров (создать, переименовать, убрать в архив, вернуть). PIN здесь не задаётся —
 * он ведётся в вкладке «Кассиры» раздела «Чеки и кассиры». Архивный кассир остаётся в списке: словом «В архиве», значком и пунктирной рамкой.
 */
export function OwnCashiersScreen({ vm, actions }: { vm: OwnCashiersVm; actions: OwnCashiersActions }) {
  const write = vm.writeDisabledReason === null
  const form = vm.form
  const archive = vm.archive
  return (
    <section className="own-screen" aria-labelledby="own-cashiers-title">
      <header className="own-toolbar">
        <h2 id="own-cashiers-title">Кассиры</h2>
        <InfoTip label="Кассиры собственной кассы">
          Кассир, которого вы заводите здесь, выбирается в РМК этой кассы, а его чеки попадают в статистику. Имя меняется свободно —
          кассир остаётся тем же. PIN кассира задаётся не здесь, а в вкладке «Кассиры» раздела «Чеки и кассиры».
        </InfoTip>
        {form === null && (
          <Button variant="primary" icon={<Plus size={ICON_SIZE} aria-hidden="true" />} disabled={!write} onClick={actions.create}>
            Новый кассир
          </Button>
        )}
        <Button variant="ghost" icon={<KeyRound size={ICON_SIZE} aria-hidden="true" />} onClick={actions.openAccess}>
          Задать PIN в «Доступе кассиров»
        </Button>
        <Button variant="ghost" icon={<RefreshCw size={ICON_SIZE} aria-hidden="true" />} busy={vm.query.refreshing} onClick={actions.reload}>
          Обновить
        </Button>
      </header>
      {vm.writeDisabledReason !== null && (
        <p className="own-muted" role="note">
          Создание и правка выключены. {vm.writeDisabledReason}
        </p>
      )}
      {vm.done !== null && (
        <p className="own-problem" role="status">
          <CircleCheck size={ICON_SIZE_SMALL} aria-hidden="true" />
          {vm.done}
        </p>
      )}
      {vm.warning !== null && (
        <p className="own-problem" role="alert">
          <CircleAlert size={ICON_SIZE_SMALL} aria-hidden="true" />
          {vm.warning}
        </p>
      )}
      {vm.rowError !== null && <CommandError title={vm.rowError.title} error={vm.rowError.view} />}
      {form !== null && (
        <section className="own-section" aria-labelledby="own-cashier-form-title">
          <div className="own-section__head">
            <h3 id="own-cashier-form-title">{form.draft.id === null ? 'Новый кассир' : 'Изменить имя кассира'}</h3>
            <Button variant="ghost" icon={<X size={ICON_SIZE} aria-hidden="true" />} onClick={actions.closeForm}>
              Закрыть
            </Button>
          </div>
          <div className="own-row">
            <TextField label="Имя кассира" value={form.draft.name} onChange={(name) => actions.draftChanged({ ...form.draft, name })} />
          </div>
          <Problem text={form.problem} />
          {form.error !== null && <CommandError title="Кассир не записан" error={form.error} />}
          <div className="own-row">
            <Button
              variant="primary"
              icon={<Save size={ICON_SIZE} aria-hidden="true" />}
              busy={form.saving}
              disabled={!write || form.problem !== null}
              onClick={actions.save}
            >
              {form.draft.id === null ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </section>
      )}
      {archive !== null && (
        <section className="own-section" aria-labelledby="own-cashier-archive-title">
          <h3 id="own-cashier-archive-title">Убрать «{archive.name}» в архив?</h3>
          <p className="own-muted">
            Кассир перестанет выбираться в РМК, а его действующий доступ (допуск по PIN) будет отозван. Прошлые чеки останутся за ним.
            Вернуть кассира можно в любой момент; новый PIN задаётся в вкладке «Кассиры» раздела «Чеки и кассиры».
          </p>
          {archive.revokeWarning !== null && (
            <p className="own-problem" role="alert">
              <CircleAlert size={ICON_SIZE_SMALL} aria-hidden="true" />
              {archive.revokeWarning}
            </p>
          )}
          {archive.stage !== null && (
            <p className="own-muted" role="status">
              {archive.stage}
            </p>
          )}
          {archive.error !== null && <CommandError title={archive.error.title} error={archive.error.view} />}
          <div className="own-row">
            <Button variant="danger" icon={<Archive size={ICON_SIZE} aria-hidden="true" />} busy={archive.working} disabled={!write} onClick={actions.confirmArchive}>
              В архив
            </Button>
            <Button variant="ghost" disabled={archive.working} onClick={actions.cancelArchive}>
              Не архивировать
            </Button>
          </div>
        </section>
      )}
      <QueryState query={vm.query} emptyText="Кассиров пока нет — заведите первого кнопкой «Новый кассир»" onRetry={actions.reload} what="Кассиры" />
      {vm.rows.length > 0 && (
        <ul className="own-list" aria-label="Кассиры">
          {vm.rows.map((row) => (
            <li key={row.id} data-archived={row.archived}>
              {row.archived ? <Archive size={ICON_SIZE} aria-hidden="true" /> : <UserRound size={ICON_SIZE} aria-hidden="true" />}
              <span>{row.name}</span>
              <span className="own-badge" data-kind={row.archived ? undefined : 'current'}>
                {row.statusText}
              </span>
              <span className="own-list__meta">{row.revisionText}</span>
              <Button variant="ghost" icon={<Pencil size={ICON_SIZE} aria-hidden="true" />} disabled={!write || row.archived} onClick={() => actions.edit(row.id)}>
                Переименовать
              </Button>
              {row.archived ? (
                <Button variant="ghost" icon={<ArchiveRestore size={ICON_SIZE} aria-hidden="true" />} disabled={!write} onClick={() => actions.restore(row.id)}>
                  Вернуть
                </Button>
              ) : (
                <Button variant="ghost" icon={<Archive size={ICON_SIZE} aria-hidden="true" />} disabled={!write} onClick={() => actions.askArchive(row.id)}>
                  В архив
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
